import { Directive, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';

import { UserLocation } from '../../models/user/location.model';
import * as fromRoot from '../../reducers';
import { SaveFavoriteLocationsAction } from '../../actions/settings';
import {
  compareLocationsByNumber,
  getDisplayLocations$,
  getFilteredLocations$,
  getSelectedLocationDisplayName,
  shouldUseLocationSearch,
  toggleFavoriteLocation,
} from './location-utils';

@Directive()
export abstract class LocationPickerBase implements OnInit, OnDestroy {
  public locations: UserLocation[];
  public locations$: Observable<UserLocation[]>;
  public selectedLocation$: Observable<UserLocation>;
  public selectedLocation: UserLocation;
  public favoriteLocationNumbers$: Observable<string[]>;
  public favoriteLocationNumbers: string[] = [];
  public searchTerm = '';
  public currentPage = 0;
  public pageSize = 5;

  public readonly compareLocations = compareLocationsByNumber;
  public readonly shouldUseLocationSearch = shouldUseLocationSearch;

  private locationSub: Subscription;
  private selectedLocationSub: Subscription;
  private favoriteLocationNumbersSub: Subscription;

  constructor(protected readonly store: Store<fromRoot.State>) {}

  public ngOnInit(): void {
    this.selectedLocation$ = this.store.select(fromRoot.getSelectedLocation);
    this.locations$ = this.store.select(fromRoot.getUserLocations);
    this.favoriteLocationNumbers$ = this.store.select(fromRoot.getFavoriteLocations);

    this.locationSub = this.locations$.subscribe((locations) => {
      this.locations = locations;
    });
    this.selectedLocationSub = this.selectedLocation$.subscribe((location) => {
      this.selectedLocation = location;
    });
    this.favoriteLocationNumbersSub = this.favoriteLocationNumbers$.subscribe((favoriteLocationNumbers) => {
      this.favoriteLocationNumbers = favoriteLocationNumbers;
    });
  }

  public ngOnDestroy(): void {
    this.locationSub?.unsubscribe();
    this.selectedLocationSub?.unsubscribe();
    this.favoriteLocationNumbersSub?.unsubscribe();
  }

  public get isMultiLocation(): boolean {
    return !!this.locations && this.locations.length > 1;
  }

  public get selectedLocationDisplayName(): string {
    return getSelectedLocationDisplayName(this.selectedLocation, this.locations);
  }

  public getFilteredLocations$(): Observable<UserLocation[]> {
    return getFilteredLocations$(this.locations$, this.searchTerm);
  }

  public getDisplayLocations$(): Observable<UserLocation[]> {
    return getDisplayLocations$(
      this.locations$,
      this.selectedLocation,
      this.favoriteLocationNumbers,
      this.searchTerm,
      this.currentPage,
      this.pageSize,
    );
  }

  public onSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
    this.currentPage = 0;
  }

  public pageLeft(): void {
    this.currentPage--;
  }

  public pageRight(): void {
    this.currentPage++;
  }

  public clearLocationFiltering(): void {
    this.currentPage = 0;
    this.searchTerm = '';
  }

  public toggleFavorite(location: UserLocation): void {
    this.store.dispatch(new SaveFavoriteLocationsAction({
      favoriteLocations: toggleFavoriteLocation(this.favoriteLocationNumbers, location.locationNumber)
    }));
  }
}
